import React, { useState, useContext } from 'react';
import { CartContext } from '../Context/CartContext';
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronRight, X } from 'lucide-react';
import AddToCartPopup from '../Components/AddToCartPopup';

const ProductPage = () => {
    const { state } = useLocation();
    const { project } = state;
    const { addToCart } = useContext(CartContext);
    const navigate = useNavigate();

    const [selectedSize, setSelectedSize] = useState(project.sizes[0]);
    const [selectedColor, setSelectedColor] = useState('ללא');
    const [selectedWoodType, setSelectedWoodType] = useState(project.woodTypes[0]);
    const [mainImg, setMainImg] = useState(project.mainImage);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [currentImage, setCurrentImage] = useState(null);
    const [popupVisible, setPopupVisible] = useState(false);
    const [addedItem, setAddedItem] = useState(null);

    const colors = [
        { name: 'ללא', code: '#dfdede' },
        { name: 'לבן', code: '#fff' },
        { name: 'אגוז', code: '#4A3B32' },
        { name: 'אלון', code: '#C19B52' }
    ];

    const handleSizeChange = (e) => setSelectedSize(e.target.value);
    const handleColorChange = (color) => setSelectedColor(color.name);
    const handleWoodTypeChange = (e) => setSelectedWoodType(e.target.value);

    const calculatePrice = () => project.basePrice || project.price;

    const goBack = () => navigate(-1);
    const openModal = (image) => {
        setCurrentImage(image);
        setModalOpen(true);
    };
    const closeModal = () => {
        setModalOpen(false);
        setCurrentImage(null);
    };
    const handleClosePopup = () => setPopupVisible(false);
    const handleContinueShopping = () => setPopupVisible(false);
    const handleProceedToCheckout = () => {
        setPopupVisible(false);
        navigate('/orders');
    };

    const handleAddToCart = () => {
        const newItem = {
            id: `${project.title}-${selectedSize}-${selectedColor}-${selectedWoodType}`,
            title: project.title,
            size: selectedSize,
            color: colors.find(c => c.name === selectedColor) || { name: 'ללא', code: 'none' },
            woodType: selectedWoodType,
            price: calculatePrice(),
            image: mainImg,
        };
        addToCart(newItem);
        setAddedItem(newItem);
        setShowSuccessMessage(true);
        setTimeout(() => {
            setShowSuccessMessage(false);
            setPopupVisible(true);
        }, 1000);
    };

    return (
        // הסרנו את ה-pt המוגזם, החלפנו ל-py-12 (ריווח טבעי של הדף)
        <div className="min-h-screen bg-background py-10 sm:py-10 px-4 sm:px-6 lg:px-8" dir="rtl">
            <div className="max-w-7xl mx-auto">

                <div className="flex flex-col gap-4 mb-10">
                    <button
                        onClick={goBack}
                        className="flex items-center gap-2 text-text-secondary hover:text-primary transition-colors duration-300 group w-fit text-sm font-medium"
                    >
                        <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                        <span>חזרה לקטלוג</span>
                    </button>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-text-primary border-r-4 border-accent pr-4 py-1">
                        {project.title}
                    </h1>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">

                    <div className="lg:col-span-7 space-y-4">
                        <div className="bg-surface rounded-2xl overflow-hidden shadow-soft border border-secondary/50">
                            <img
                                src={mainImg}
                                alt={project.title}
                                className="w-full h-[400px] sm:h-[550px] object-cover cursor-pointer transition-transform duration-500 hover:scale-[1.02]"
                                onClick={() => openModal(mainImg)}
                            />
                        </div>
                        <div className="grid grid-cols-4 gap-3 sm:gap-4">
                            {project.galleryImages.map((image, index) => (
                                <button
                                    key={index}
                                    className={`bg-surface rounded-xl overflow-hidden transition-all duration-300
                                        ${mainImg === image
                                        ? 'border-2 border-accent shadow-md opacity-100'
                                        : 'border border-secondary/50 opacity-70 hover:opacity-100 hover:border-accent/50'
                                    }`}
                                    onClick={() => setMainImg(image)}
                                >
                                    <img
                                        src={image}
                                        alt={`${project.title} ${index + 1}`}
                                        className="w-full h-20 sm:h-28 object-cover"
                                    />
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-5">
                        <div className="bg-surface rounded-2xl shadow-soft border border-secondary/50 p-6 sm:p-8 sticky top-32">
                            <div className="space-y-8">

                                <div className="space-y-3">
                                    <label className="text-sm font-medium text-text-primary">מידות מבוקשות:</label>
                                    <select
                                        value={selectedSize}
                                        onChange={handleSizeChange}
                                        className="w-full px-4 py-3 text-base rounded-xl border border-secondary-dark bg-background focus:bg-surface focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                                    >
                                        {project.sizes.map((size, index) => (
                                            <option key={index} value={size}>{size}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="space-y-3">
                                    <label className="text-sm font-medium text-text-primary">גוון עץ/צבע:</label>
                                    <div className="grid grid-cols-2 gap-3">
                                        {colors.map((color, index) => (
                                            <button
                                                key={index}
                                                onClick={() => handleColorChange(color)}
                                                className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all text-sm font-medium
                                                    ${selectedColor === color.name
                                                    ? 'border-accent bg-accent/5 text-text-primary shadow-sm'
                                                    : 'border-secondary-dark text-text-secondary hover:border-accent/40 hover:bg-background'
                                                }`}
                                            >
                                                <span
                                                    className="w-5 h-5 rounded-full border border-secondary shadow-sm"
                                                    style={{ backgroundColor: color.code }}
                                                />
                                                <span>{color.name}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <label className="text-sm font-medium text-text-primary">סוג עץ:</label>
                                    <select
                                        value={selectedWoodType}
                                        onChange={handleWoodTypeChange}
                                        className="w-full px-4 py-3 text-base rounded-xl border border-secondary-dark bg-background focus:bg-surface focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                                    >
                                        {project.woodTypes.map((woodType, index) => (
                                            <option key={index} value={woodType}>{woodType}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="pt-8 border-t border-secondary mt-8">
                                    <div className="flex justify-between items-end mb-8">
                                        <span className="text-lg font-medium text-text-secondary">מחיר משוער:</span>
                                        <span className="text-3xl font-bold text-accent">
                                            {project.price === 'בהתאמה אישית' ? 'ייקבע בייעוץ' : project.price}
                                        </span>
                                    </div>

                                    <button
                                        onClick={handleAddToCart}
                                        className="w-full px-8 py-4 bg-primary text-white text-lg font-medium rounded-xl hover:bg-primary-light transition-all duration-300 shadow-soft hover:shadow-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                                    >
                                        הוסף לפרויקטים שלי
                                    </button>

                                    {showSuccessMessage && (
                                        <p className="text-center text-primary mt-4 text-sm font-medium animate-fade-in">
                                            הפריט נוסף לסל ההצעות בהצלחה!
                                        </p>
                                    )}

                                    <p className="text-sm text-text-muted text-center mt-6">
                                        * התמחור הסופי ייקבע לאחר מדידות בשטח ובחירת מפרט סופי. המחירים אינם כוללים מע"מ.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {modalOpen && (
                <div
                    className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60] p-4 animate-fade-in"
                    onClick={closeModal}
                >
                    <div className="relative max-w-5xl w-full flex justify-center">
                        <button
                            onClick={closeModal}
                            className="absolute -top-12 right-0 text-white/70 hover:text-white transition-colors"
                        >
                            <X className="w-8 h-8" />
                        </button>
                        <img
                            src={currentImage}
                            alt="תמונה מוגדלת"
                            className="max-h-[85vh] w-auto object-contain rounded-md shadow-2xl"
                        />
                    </div>
                </div>
            )}

            {popupVisible && (
                <AddToCartPopup
                    item={addedItem}
                    onClose={handleClosePopup}
                    onContinueShopping={handleContinueShopping}
                    onProceedToCheckout={handleProceedToCheckout}
                />
            )}
        </div>
    );
};

export default ProductPage;